import { getSingleCustomerSearchAsync } from "api/customers";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { CancelledError, isCancelledError, useQuery } from "react-query";

export const withCustomerSearch = (WrappedComponent) => {
  return function WithCustomerSearch(props) {
    const pendingRequest = useRef();
    const [searchParams, setSearchParams] = useState({
      includeTeam: false,
      disabled: false,
      search: "",
      allUsers: false,
      clients: false,
      clinicians: false,
      deactivated: false,
      parents: false,
    });

    const { data: searchResults, isLoading: searchLoading } = useQuery({
      queryKey: ["searchCustomers", { ...searchParams }],
      queryFn: () => {
        pendingRequest.current?.cancel("Superseded by a newer search");
        const requestSource = axios.CancelToken.source();
        pendingRequest.current = requestSource;
        const request = getSingleCustomerSearchAsync(
          {
            disabled: searchParams.disabled,
            includeTeam: searchParams.includeTeam,
            search: searchParams.search,
            allUsers: searchParams.allUsers,
            clients: searchParams.clients,
            clinicians: searchParams.clinicians,
            deactivated: searchParams.deactivated,
            parents: searchParams.parents,
          },
          { cancelToken: requestSource.token }
        ).catch((error) => {
          if (axios.isCancel(error)) {
            throw new CancelledError({ silent: true });
          }
          throw error;
        });
        request.then(
          () => {
            if (pendingRequest.current === requestSource) {
              pendingRequest.current = undefined;
            }
          },
          () => {
            if (pendingRequest.current === requestSource) {
              pendingRequest.current = undefined;
            }
          }
        );
        return request;
      },
      enabled: searchParams.search.length >= 2,
      retry: (failureCount, error) =>
        !axios.isCancel(error) && !isCancelledError(error) && failureCount < 3,
    });

    useEffect(
      () => () => {
        pendingRequest.current?.cancel("Search component unmounted");
      },
      []
    );

    const getCustomers = (search, filters = {}) => {
      setSearchParams({
        ...search,
        ...filters,
      });
    };

    const data = searchResults?.data ?? [];

    const clearSearchResults = () => {
      pendingRequest.current?.cancel("Search cleared");
      setSearchParams({
        includeTeam: false,
        disabled: false,
        search: "",
        allUsers: false,
        clients: false,
        clinicians: false,
        deactivated: false,
        parents: false,
      });
    };

    return (
      <WrappedComponent
        {...props}
        customerSearchResults={data}
        customerSearchLoading={searchLoading}
        clearSearchResults={clearSearchResults}
        getCustomers={getCustomers}
      />
    );
  };
};

export default withCustomerSearch;
